/**
 * The centers table.
 *
 * This is the flat view of the fleet — the ports, database and S3 prefix that
 * must not collide between centers. Every column sorts, which is how a
 * duplicate is found by eye, and the search narrows the list.
 *
 * The redesign joins two things the API keeps apart. `GET /api/centers` returns
 * *declared* configuration and `GET /api/fleet/centers` returns the *live*
 * probe; on their own, one table tells you what a center should be and another
 * tells you whether it answers. Joined on `slug`, a row finally says both — and
 * the difference between them is where nearly every real incident lives.
 *
 * Two honesty rules carried over. A center the probe never reached shows
 * "Unknown", never a zero — `format.ts` returns an em dash for null and that is
 * load-bearing here. And "Checked" is the time *the controller probed*, not a
 * check-in from the center: centers do not report, they are polled, and naming
 * the column "Last seen" would imply a heartbeat that does not exist.
 *
 * ── Compaction pass ─────────────────────────────────────────────────────────
 * What changed, and why:
 *
 *   - The header's descriptive paragraph is gone. It explained the join to a
 *     reader who has already read it; the operator who lives here has not. The
 *     title, the serving badge, Freshness and "Add center" remain.
 *   - A KPI strip sits above the filter bar. The first four tiles are the
 *     SegmentedFilter's four states rendered as `<button className="metric">` —
 *     buttons, not links, because they set local state and go nowhere, and a
 *     link that cannot be opened in a new tab is a lie to the middle mouse
 *     button. Pressing an active *state* tile again clears back to All. The
 *     last two tiles are read-only aggregates: there is no "slow" or "busy"
 *     state for the table to be narrowed to, so they do not pretend to filter.
 *   - Both aggregates obey the page's own null rule. Average latency is the
 *     mean of the centers that *answered*; the ones that did not are excluded
 *     from the divisor rather than counted as 0 ms, and if none answered the
 *     tile reads UNKNOWN. Total studies is the same shape. Each tile's hint
 *     names how many centers it is actually made of, so a number computed from
 *     three of twenty centers cannot be mistaken for a fleet total.
 *   - There is deliberately no storage-headroom KPI. The API reports a disk
 *     size and no capacity or threshold, so any "nearly full" tile would be
 *     this page's guess wearing the fleet's telemetry.
 *   - The Domain cell lost its second line. "doctor 1908" is the first label of
 *     the domain printed directly above it, so the row was spending a line to
 *     repeat itself; the value keeps its own labelled row in the detail panel.
 *   - The search placeholder no longer promises database and S3-prefix
 *     matching. DataTable searches the columns it is given and neither is a
 *     column, so those two words never did anything.
 */

import { useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { Badge, SeverityBadge, StatusBadge } from '../components/Badge';
import { Freshness } from '../components/Freshness';
import { PageHeader } from '../components/PageHeader';
import { StatGrid, StatTile, type MetricTone } from '../components/StatTile';
import { EmptyBlock, ErrorBlock, SkeletonRows, StaleBanner } from '../components/States';
import { Card, KeyValue, KeyValueRow } from '../components/ui/Card';
import { DataTable, type Column } from '../components/ui/DataTable';
import { DetailPanel } from '../components/ui/DetailPanel';
import { FilterBar, SegmentedFilter } from '../components/ui/FilterBar';
import { AddIcon, ChevronRightIcon } from '../components/ui/Icon';
import {
  formatBytes,
  formatLatency,
  formatMegabytes,
  formatNumber,
  formatRelative,
  UNKNOWN,
} from '../lib/format';
import { Sparkline } from '../components/viz/Chart';
import { describeError } from '../lib/api';
import { addDays, baghdadToday } from '../lib/baghdad';
import { useResource, type ResourceState } from '../lib/useResource';
import type {
  CenterListResponse,
  CenterSeriesResponse,
  CenterSummary,
  EdgeNodeListResponse,
  FleetHealthResponse,
  IssuesResponse,
  OrthancHealth,
  ServiceState,
} from '../types/api';

import '../styles/page-centers.css';

const REFRESH_MS = 30_000;

/** A center as this page understands it: declared config plus what it is doing. */
interface CenterRow extends CenterSummary {
  health: OrthancHealth | undefined;
  state: ServiceState | 'unknown';
  /** Null when the registry could not be read: unknown, not zero. */
  edgeNodes: number | null;
  /** Why `edgeNodes` is unknown ('' when it is known). */
  edgeNodesReason: string;
  blocking: number;
  warnings: number;
}

type StatusFilter = 'all' | 'serving' | 'not-serving' | 'flagged';

/** Blocking findings dominate the sort; ties fall through to warnings. */
function findingWeight(center: CenterSummary): number {
  const counts = center.finding_counts ?? {};
  return (
    (counts.block ?? 0) * 1_000_000 + (counts.warn ?? 0) * 1_000 + (counts.info ?? 0)
  );
}

function FindingCell({ center }: { center: CenterRow }) {
  const counts = center.finding_counts ?? {};
  const block = counts.block ?? 0;
  const warn = counts.warn ?? 0;
  const info = counts.info ?? 0;

  if (block + warn + info === 0) return <span className="muted">None</span>;
  return (
    <span className="row">
      {block > 0 ? <Badge tone="critical" small>{block} blocking</Badge> : null}
      {warn > 0 ? <Badge tone="warning" small>{warn} warning</Badge> : null}
      {info > 0 ? <Badge tone="info" small>{info} info</Badge> : null}
    </span>
  );
}

/**
 * A KPI tile that is also the status filter.
 *
 * Wears StatTile's markup so it reads as one of the strip, but it is a real
 * `<button>`: it sets this page's filter state and navigates nowhere, and
 * `aria-pressed` is what tells a screen reader which of the four is currently
 * narrowing the table. `metric__head` is a span rather than StatTile's div
 * because a button may only contain phrasing content.
 */
function FilterTile({
  label,
  value,
  hint,
  tone,
  active,
  onClick,
}: {
  label: string;
  value: ReactNode;
  hint: ReactNode;
  tone?: MetricTone;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={`metric centers-kpi${tone ? ` metric--${tone}` : ''}`}
      aria-pressed={active}
      onClick={onClick}
    >
      <span className="metric__head">
        <span className="metric__label">{label}</span>
      </span>
      <span className="metric__value">{value}</span>
      <span className="metric__hint">{hint}</span>
    </button>
  );
}

/**
 * The mean and the sum of whatever actually reported, plus how many did.
 *
 * A center whose probe failed contributes nothing — not a zero — so it is
 * neither summed nor counted in the divisor. `sampled` travels with the number
 * because a mean over three of twenty centers and a mean over twenty are
 * different facts wearing the same digits, and only the tile's hint can say
 * which one you are looking at.
 */
function aggregate(values: ReadonlyArray<number | null | undefined>): {
  total: number | null;
  average: number | null;
  sampled: number;
} {
  const known = values.filter(
    (value): value is number => typeof value === 'number' && Number.isFinite(value),
  );
  if (known.length === 0) return { total: null, average: null, sampled: 0 };
  const total = known.reduce((sum, value) => sum + value, 0);
  return { total, average: total / known.length, sampled: known.length };
}

export function CentersPage() {
  const centers = useResource<CenterListResponse>('/api/centers');
  const health = useResource<FleetHealthResponse>('/api/fleet/centers', {
    refreshMs: REFRESH_MS,
  });
  // Derived, not stored: the registry keys nodes by doctor_id, which is the
  // center's subdomain label (Contract A), and the server already resolves it
  // to `center_slug`. Counting them here costs one request the page already
  // wants and saves inventing an endpoint.
  const edge = useResource<EdgeNodeListResponse>('/api/edge-nodes');
  const issues = useResource<IssuesResponse>('/api/issues');

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<StatusFilter>('all');
  const [openSlug, setOpenSlug] = useState<string | null>(null);

  const healthBySlug = useMemo(
    () => new Map((health.data?.centers ?? []).map((c) => [c.slug, c])),
    [health.data],
  );

  const nodesBySlug = useMemo(() => {
    const counts = new Map<string, number>();
    for (const node of edge.data?.nodes ?? []) {
      if (!node.center_slug) continue;
      counts.set(node.center_slug, (counts.get(node.center_slug) ?? 0) + 1);
    }
    return counts;
  }, [edge.data]);

  // An unreadable registry (or a failed request) is "not known", never 0.
  const edgeUnknownReason = !edge.data
    ? edge.error
      ? describeError(edge.error)
      : 'The edge registry has not loaded yet.'
    : edge.data.error !== ''
      ? edge.data.error
      : '';

  const rows = useMemo<CenterRow[]>(() => {
    return (centers.data?.centers ?? []).map((center) => {
      const h = healthBySlug.get(center.slug);
      const counts = center.finding_counts ?? {};
      return {
        ...center,
        health: h,
        state: h?.state ?? 'unknown',
        edgeNodes: edgeUnknownReason ? null : nodesBySlug.get(center.slug) ?? 0,
        edgeNodesReason: edgeUnknownReason,
        blocking: counts.block ?? 0,
        warnings: counts.warn ?? 0,
      };
    });
  }, [centers.data, healthBySlug, nodesBySlug, edgeUnknownReason]);

  const counts = useMemo(
    () => ({
      all: rows.length,
      serving: rows.filter((r) => r.state === 'up').length,
      notServing: rows.filter((r) => r.state === 'down' || r.state === 'degraded').length,
      flagged: rows.filter((r) => r.blocking > 0 || r.health?.doctor_row_present === false)
        .length,
      probed: rows.filter((r) => r.health !== undefined).length,
    }),
    [rows],
  );

  const latency = useMemo(() => aggregate(rows.map((r) => r.health?.latency_ms)), [rows]);
  const studies = useMemo(() => aggregate(rows.map((r) => r.health?.studies)), [rows]);

  const filtered = useMemo(() => {
    switch (status) {
      case 'serving':
        return rows.filter((r) => r.state === 'up');
      case 'not-serving':
        return rows.filter((r) => r.state === 'down' || r.state === 'degraded');
      case 'flagged':
        return rows.filter((r) => r.blocking > 0 || r.health?.doctor_row_present === false);
      default:
        return rows;
    }
  }, [rows, status]);

  const open = openSlug ? rows.find((r) => r.slug === openSlug) : undefined;
  const openIssues = (issues.data?.findings ?? []).filter(
    (f) => f.center_slug === openSlug,
  );

  // One extra request for the whole table rather than one per row. It is
  // supplementary: the table renders without it, and a row whose history has
  // not arrived (or was never recorded) shows nothing rather than a flat line
  // at zero, which would read as "no studies" instead of "not known".
  //
  // The window start is fixed once per mount. Computed inline it changed on
  // every render, so the URL changed, useResource refetched, the response
  // re-rendered, and the page polled this route several times a second.
  //
  // A Baghdad day (YYYY-MM-DD), inclusive: today and the six before it are
  // seven days, matching the "7-day" sparkline.
  const [historyFrom] = useState(() => addDays(baghdadToday(), -6));
  const history = useResource<CenterSeriesResponse>(
    '/api/reports/centers?bucket=day&per_center=true&from=' +
      encodeURIComponent(historyFrom),
  );

  const studiesBySlug = useMemo(() => {
    const bySlug = new Map<string, (number | null)[]>();
    for (const point of history.data?.points ?? []) {
      if (!point.slug) continue;
      const series = bySlug.get(point.slug) ?? [];
      series.push(point.studies);
      bySlug.set(point.slug, series);
    }
    return bySlug;
  }, [history.data]);

  const columns: ReadonlyArray<Column<CenterRow, string>> = useMemo(
    () => [
      {
        key: 'slug',
        header: 'Center',
        render: (row) => (
          <div className="cell-stack">
            <Link to={`/centers/${row.slug}`} className="cell-stack__primary">
              {row.slug}
            </Link>
            <span className="cell-stack__secondary">{row.name ?? 'Unnamed'}</span>
          </div>
        ),
      },
      {
        key: 'domain',
        // One line: the doctor id is this domain's own first label, so printing
        // it underneath spent a row height repeating the cell above it. The
        // detail panel still names it as its own labelled value.
        header: 'Domain',
        render: (row) => <span className="mono centers-domain">{row.domain}</span>,
      },
      {
        key: 'region',
        header: 'Region',
        render: (row) =>
          row.region ? (
            <span className="chip">{row.region}</span>
          ) : (
            // "" is ungrouped, a real state — not unknown, so not an em dash.
            <span className="muted">Ungrouped</span>
          ),
      },
      {
        key: 'state',
        header: 'Service',
        render: (row) => (
          <span className="row">
            <StatusBadge state={row.state as ServiceState} />
            {row.health?.doctor_row_present === false ? (
              <Badge tone="warning" small>No doctor row</Badge>
            ) : null}
          </span>
        ),
      },
      {
        key: 'latency',
        header: 'Latency',
        numeric: true,
        value: (row) => row.health?.latency_ms ?? null,
        render: (row) => formatLatency(row.health?.latency_ms),
      },
      {
        key: 'studies',
        header: 'Studies',
        numeric: true,
        value: (row) => row.health?.studies ?? null,
        render: (row) => formatNumber(row.health?.studies),
      },
      {
        key: 'trend',
        header: '7-day studies',
        // Sorted by the latest value in the series, so the column orders by
        // something the shape actually shows rather than by nothing.
        value: (row) => {
          const series = studiesBySlug.get(row.slug) ?? [];
          const last = [...series].reverse().find((v) => v !== null);
          return last ?? null;
        },
        render: (row) => (
          <Sparkline
            points={studiesBySlug.get(row.slug) ?? []}
            label={`Studies on ${row.slug} over the last 7 days`}
          />
        ),
      },
      {
        key: 'storage',
        header: 'Storage',
        numeric: true,
        value: (row) => row.health?.disk_size_bytes ?? null,
        render: (row) => formatBytes(row.health?.disk_size_bytes),
      },
      {
        key: 'ports',
        header: 'Ports',
        value: (row) => row.web_port,
        render: (row) => (
          <span className="mono cell-ports">
            {row.web_port}
            <span className="muted"> / </span>
            {row.dicom_port}
          </span>
        ),
      },
      {
        key: 'edgeNodes',
        header: 'Nodes',
        numeric: true,
        render: (row) =>
          row.edgeNodes === null ? (
            <span className="muted" title={row.edgeNodesReason}>
              {UNKNOWN}
            </span>
          ) : row.edgeNodes === 0 ? (
            <span className="muted">—</span>
          ) : (
            formatNumber(row.edgeNodes)
          ),
      },
      {
        key: 'findings',
        header: 'Findings',
        value: findingWeight,
        render: (row) => <FindingCell center={row} />,
      },
      {
        key: 'checked',
        header: 'Checked',
        value: (row) => row.health?.checked_at ?? null,
        render: (row) => (
          <span className="muted">{formatRelative(row.health?.checked_at)}</span>
        ),
      },
    ],
    [studiesBySlug],
  );

  const stale = centers.error && centers.data;

  return (
    <>
      <PageHeader
        title="Centers"
        status={
          centers.data ? (
            <Badge tone={counts.notServing > 0 ? 'critical' : 'good'}>
              {counts.serving}/{counts.all} serving
            </Badge>
          ) : null
        }
        actions={
          <>
            <Freshness
              checkedAt={health.data?.checked_at ?? null}
              refreshing={health.refreshing}
              onRefresh={() => {
                centers.reload();
                health.reload();
              }}
              staleAfterMs={REFRESH_MS * 3}
            />
            {/* A navigation, so a real link — a <button> here would break
                middle-click, open-in-new-tab and the browser's own history. */}
            <Link to="/centers/new" className="btn btn--primary btn--sm">
              <AddIcon size={14} className="btn__icon" />
              Add center
            </Link>
          </>
        }
      />

      {stale ? <StaleBanner error={centers.error} onRetry={centers.reload} /> : null}

      {centers.loading && !centers.data ? (
        <SkeletonRows rows={5} />
      ) : centers.error && !centers.data ? (
        <ErrorBlock what="the centers list" error={centers.error} onRetry={centers.reload} />
      ) : rows.length === 0 ? (
        <EmptyBlock
          title="No centers are defined"
          action={
            <Link to="/centers/new" className="btn btn--primary btn--md">
              Add the first center
            </Link>
          }
        >
          The controller read its fleet configuration but found no centers in it. This is
          the fleet&rsquo;s own configuration, not a permissions problem.
        </EmptyBlock>
      ) : (
        <>
          {/* The wrapper exists so `page-centers.css` can tune the whole strip
              — buttons and read-only tiles alike — without redefining a shared
              class. `StatGrid` takes no className. */}
          <div className="centers-kpis">
            <StatGrid>
              <FilterTile
                label="Centers"
                value={formatNumber(counts.all)}
                hint={`${formatNumber(counts.probed)} probed`}
                active={status === 'all'}
                onClick={() => setStatus('all')}
              />
              <FilterTile
                label="Serving"
                value={formatNumber(counts.serving)}
                hint="answering the probe"
                active={status === 'serving'}
                onClick={() => setStatus(status === 'serving' ? 'all' : 'serving')}
              />
              <FilterTile
                label="Not serving"
                value={formatNumber(counts.notServing)}
                hint="down or degraded"
                tone={counts.notServing > 0 ? 'critical' : undefined}
                active={status === 'not-serving'}
                onClick={() => setStatus(status === 'not-serving' ? 'all' : 'not-serving')}
              />
              <FilterTile
                label="Flagged"
                value={formatNumber(counts.flagged)}
                hint="blocking finding or no doctor row"
                tone={counts.flagged > 0 ? 'warning' : undefined}
                active={status === 'flagged'}
                onClick={() => setStatus(status === 'flagged' ? 'all' : 'flagged')}
              />
              {/* Read-only, and not filters: there is no "slow" or "busy" state
                  for the table to be narrowed to. */}
              <StatTile
                label="Avg latency"
                value={formatLatency(latency.average)}
                hint={
                  latency.sampled === 0
                    ? 'no center answered'
                    : `mean of ${formatNumber(latency.sampled)}`
                }
              />
              <StatTile
                label="Studies"
                value={formatNumber(studies.total)}
                hint={
                  studies.sampled === 0
                    ? 'none reported'
                    : `${formatNumber(studies.sampled)} of ${formatNumber(counts.all)} reporting`
                }
              />
            </StatGrid>
          </div>

          <FilterBar
            search={search}
            onSearch={setSearch}
            searchPlaceholder="Search slug, name, domain…"
            searchLabel="Search centers"
            count={
              filtered.length === rows.length
                ? `${formatNumber(rows.length)} centers`
                : `${formatNumber(filtered.length)} of ${formatNumber(rows.length)}`
            }
          >
            <SegmentedFilter<StatusFilter>
              legend="Filter by service state"
              value={status}
              onChange={setStatus}
              options={[
                { value: 'all', label: 'All', count: counts.all },
                { value: 'serving', label: 'Serving', count: counts.serving },
                { value: 'not-serving', label: 'Not serving', count: counts.notServing },
                { value: 'flagged', label: 'Flagged', count: counts.flagged },
              ]}
            />
          </FilterBar>

          <Card flush>
            <DataTable<CenterRow, string>
              rows={filtered}
              columns={columns}
              getRowId={(row) => row.slug}
              label="Centers"
              initialSortKey="slug"
              search={search}
              onRowClick={(row) => setOpenSlug(row.slug)}
              isRowActive={(row) => row.slug === openSlug}
              emptyFiltered={
                <EmptyBlock title="Nothing matches those filters">
                  {search ? (
                    <>
                      No center matches <strong>{search}</strong>
                      {status !== 'all' ? ' with that service state' : ''}.
                    </>
                  ) : (
                    'No center has that service state right now.'
                  )}
                </EmptyBlock>
              }
            />
          </Card>
        </>
      )}

      <DetailPanel
        open={open !== undefined}
        title={open?.slug ?? ''}
        subtitle={open?.name ?? open?.domain}
        onClose={() => setOpenSlug(null)}
        footer={
          open ? (
            <Link to={`/centers/${open.slug}`} className="btn btn--primary btn--md">
              Open full center page
              <ChevronRightIcon size={14} />
            </Link>
          ) : null
        }
      >
        {open ? (
          <CenterPanelBody center={open} issues={openIssues} issuesResource={issues} />
        ) : null}
      </DetailPanel>
    </>
  );
}

function CenterPanelBody({
  center,
  issues,
  issuesResource,
}: {
  center: CenterRow;
  issues: IssuesResponse['findings'];
  /** The /api/issues request itself: while it loads or after it fails, "no
   *  finding names this center" would be a claim nobody checked. */
  issuesResource: ResourceState<IssuesResponse>;
}) {
  const h = center.health;
  return (
    <>
      <section className="stack">
        <h3 className="section__subtitle">Health</h3>
        <div className="row">
          <StatusBadge state={center.state as ServiceState} />
          {h?.doctor_row_present === false ? (
            <Badge tone="warning" small>No doctor row</Badge>
          ) : null}
        </div>
        {h?.error ? (
          <p className="panel-error">
            <strong>Probe error:</strong> {h.error}
          </p>
        ) : null}
        <KeyValue>
          <KeyValueRow label="Orthanc">
            <span className="mono">{h?.orthanc_version ?? UNKNOWN}</span>
          </KeyValueRow>
          <KeyValueRow label="AE title">
            <span className="mono">{h?.dicom_aet ?? UNKNOWN}</span>
          </KeyValueRow>
          <KeyValueRow label="Studies">{formatNumber(h?.studies)}</KeyValueRow>
          <KeyValueRow label="Instances">{formatNumber(h?.instances)}</KeyValueRow>
          <KeyValueRow label="Storage">{formatBytes(h?.disk_size_bytes)}</KeyValueRow>
          <KeyValueRow label="Latency">{formatLatency(h?.latency_ms)}</KeyValueRow>
          <KeyValueRow label="Checked">{formatRelative(h?.checked_at)}</KeyValueRow>
        </KeyValue>
      </section>

      <section className="stack">
        <h3 className="section__subtitle">Configuration</h3>
        <KeyValue>
          <KeyValueRow label="Domain">
            <span className="mono">{center.domain}</span>
          </KeyValueRow>
          <KeyValueRow label="Doctor id">
            <span className="mono">{center.domain_prefix}</span>
          </KeyValueRow>
          <KeyValueRow label="Web port">
            <span className="mono">{center.web_port}</span>
          </KeyValueRow>
          <KeyValueRow label="DICOM port">
            <span className="mono">{center.dicom_port}</span>
          </KeyValueRow>
          <KeyValueRow label="Database">
            <span className="mono">{center.database_name}</span>
          </KeyValueRow>
          <KeyValueRow label="S3 prefix">
            <span className="mono">{center.s3_prefix}</span>
          </KeyValueRow>
          <KeyValueRow label="Cache">{formatMegabytes(center.cache_ram_mb)}</KeyValueRow>
          <KeyValueRow label="Modalities">{formatNumber(center.modality_count)}</KeyValueRow>
          <KeyValueRow label="Peers">{formatNumber(center.peer_count)}</KeyValueRow>
          <KeyValueRow label="Edge nodes">
            {center.edgeNodes === null ? (
              <span className="muted" title={center.edgeNodesReason}>
                {UNKNOWN}
              </span>
            ) : center.edgeNodes === 0 ? (
              <span className="muted">None enrolled</span>
            ) : (
              formatNumber(center.edgeNodes)
            )}
          </KeyValueRow>
        </KeyValue>
      </section>

      <section className="stack">
        <h3 className="section__subtitle">
          Active issues
          {issues.length > 0 ? <span className="section__count"> {issues.length}</span> : null}
        </h3>
        {issuesResource.error && !issuesResource.data ? (
          <ErrorBlock
            what="the findings"
            error={issuesResource.error}
            onRetry={issuesResource.reload}
          />
        ) : !issuesResource.data ? (
          <SkeletonRows rows={2} />
        ) : issues.length === 0 ? (
          <p className="muted">No validator finding names this center.</p>
        ) : (
          <ul className="panel-issues">
            {issues.map((finding, index) => (
              <li key={`${finding.code}-${index}`} className="panel-issue">
                <div className="row">
                  <SeverityBadge severity={finding.severity as never} small />
                  <span className="mono panel-issue__code">{finding.code}</span>
                </div>
                <p className="panel-issue__message">{finding.message}</p>
                {finding.fix_hint ? (
                  <p className="panel-issue__hint">{finding.fix_hint}</p>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </section>
    </>
  );
}
